import React from 'react';

/**
 * Msg #980 — Chat WebSocket hook with polling fallback + user + global channels.
 *
 * Opens ONE persistent socket to /api/ws?room=X&user_id=Y. When the current
 * `room` changes we send {action:"subscribe", room}. Server pushes:
 *   {type:"chat_message", room, message} — new chat line for `room`
 *   {type:"whisper",  whisper: {...}}    — private whisper delivered to me
 *   {type:"presence", event, user_id, name, room, prev_room, ...} — live join/leave
 *   {type:"typing",   room, user, on}    — typing indicator for `room`
 *
 * Callers pass handler callbacks; the hook forwards each event to the matching
 * callback if present. All callbacks are optional so this stays additive —
 * legacy polling still works if the socket drops.
 */
export default function useChatWebsocket(apiBase, room, handlers) {
  const socketRef = React.useRef(null);
  const failuresRef = React.useRef(0);
  const [connected, setConnected] = React.useState(false);

  // handlers can be a plain function (legacy: onMessage only) or an object
  // { onMessage, onWhisper, onPresence, onTyping, userId }
  const handlersRef = React.useRef(handlers);
  React.useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  const wsUrl = React.useMemo(() => {
    if (!apiBase) return null;
    try {
      const u = new URL(apiBase);
      const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${u.host}${u.pathname.replace(/\/$/, '')}/ws`;
    } catch {
      return null;
    }
  }, [apiBase]);

  const userId = (handlers && typeof handlers === 'object' && handlers.userId) || null;

  React.useEffect(() => {
    if (!wsUrl || !room) return undefined;
    // Give up after 3 consecutive failures — polling picks up the slack.
    if (failuresRef.current >= 3) return undefined;

    let cancelled = false;
    let heartbeat;
    let ws;
    const url = `${wsUrl}?room=${encodeURIComponent(room)}${userId ? `&user_id=${encodeURIComponent(userId)}` : ''}`;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      failuresRef.current += 1;
      return undefined;
    }
    socketRef.current = ws;

    ws.onopen = () => {
      if (cancelled) return;
      failuresRef.current = 0;
      setConnected(true);
      heartbeat = setInterval(() => {
        try { ws.readyState === 1 && ws.send(JSON.stringify({ action: 'ping' })); }
        catch { /* ignore */ }
      }, 25000);
    };
    ws.onmessage = (ev) => {
      if (cancelled) return;
      try {
        const data = JSON.parse(ev.data);
        const h = handlersRef.current;
        // Legacy: caller passed a plain function → treat as onMessage only
        if (typeof h === 'function') {
          if (data && data.type === 'chat_message' && data.message) h(data);
          return;
        }
        if (!data || typeof h !== 'object') return;
        if (data.type === 'chat_message' && data.message && h.onMessage) h.onMessage(data);
        else if (data.type === 'whisper' && data.whisper && h.onWhisper) h.onWhisper(data.whisper);
        else if (data.type === 'presence' && h.onPresence) h.onPresence(data);
        else if (data.type === 'typing' && h.onTyping) h.onTyping(data);
        else if (data.type === 'nowplaying' && h.onNowPlaying) h.onNowPlaying(data);
      } catch { /* ignore */ }
    };
    ws.onerror = () => { failuresRef.current += 1; };
    ws.onclose = () => {
      setConnected(false);
      if (heartbeat) clearInterval(heartbeat);
    };

    return () => {
      cancelled = true;
      if (heartbeat) clearInterval(heartbeat);
      try { ws.close(); } catch { /* ignore */ }
      socketRef.current = null;
    };
  }, [wsUrl, room, userId]);

  // Msg #980 — expose a way to send typing indicators over the socket
  const sendTyping = React.useCallback((user, on) => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify({ action: 'typing', user, on: !!on }));
    } catch { /* ignore */ }
  }, []);

  return { connected, sendTyping };
}
